import React from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'RowType'. Did you mean to use 'import RowType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead?
import { DynamicTableStateless, type RowType, type HeadType } from '@atlaskit/dynamic-table';
import EmptyState from '@atlaskit/empty-state';
import ActionsCell from '@atlassian/jira-admin-pages-common/src/ui/actions-cell/index.tsx';
import EmptyIcon from '@atlassian/jira-admin-pages-common/src/ui/empty-icon/assets/empty.svg';
import ListSchemes from '@atlassian/jira-admin-pages-common/src/ui/list-schemes/index.tsx';
import NameAndDescriptionCell from '@atlassian/jira-admin-pages-common/src/ui/name-and-description-cell/index.tsx';
import {
	EMPTY_PAGE,
	EMPTY_WORKFLOWS_PAGE,
} from '@atlassian/jira-admin-screens-services/src/common/constants.tsx';
import type { Screen } from '@atlassian/jira-admin-screens-services/src/common/types.tsx';
import {
	buildConfigureScreenJSPUrl,
	buildScreenSchemeJSPUrl,
} from '@atlassian/jira-admin-screens-services/src/services/screens-service/actions/utils.tsx';
import { TableWrapper } from '@atlassian/jira-admin-screens-services/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	SCREEN,
	FireScreenAnalytics,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import {
	PAGE,
	SCREEN_STRING,
	SCREENS_COLUMN,
	SCREEN_SCHEME,
	ORDER,
	SCREEN_ACTIONS,
} from '../../constants';
import ListWorkflows from './list-workflows';
import messages from './messages';
import type { Props } from './types';
import { createHeaderRow, buildActionLinks, getAnalyticsAttributes } from './utils';

export default function ScreensTable({ filter, isLoading, total, screens, getScreens }: Props) {
	const { formatMessage } = useIntl();
	const header: HeadType = createHeaderRow(formatMessage);
	const rows: RowType[] = screens.map((screen: Screen) => {
		const {
			id,
			name,
			description,
			screenSchemes = EMPTY_PAGE,
			workflowTransitions = EMPTY_WORKFLOWS_PAGE,
		} = screen;
		return {
			key: `row-screen-${screen.id}`,
			cells: [
				{
					key: 'name',
					content: (
						<ContextualAnalyticsData
							attributes={getAnalyticsAttributes(screen)}
							containers={{ screen: { id: `${id}` } }}
						>
							<NameAndDescriptionCell
								scheme={{ id, name, description }}
								getUrl={({ id: schemeId }) => buildConfigureScreenJSPUrl(schemeId)}
								onLinkClickHandleAnalytics={(analyticsEvent) => {
									fireUIAnalytics(analyticsEvent, SCREEN_STRING);
								}}
							/>
						</ContextualAnalyticsData>
					),
				},
				{
					key: 'screenSchemes',
					content: (
						<ListSchemes
							schemes={[...screenSchemes.values]}
							getUrl={(screenScheme) => buildScreenSchemeJSPUrl(screenScheme.id)}
							getAnalyticsContainers={(screenScheme) => ({
								screen: { id: `${id}` },
								screenScheme: { id: `${screenScheme.id}` },
							})}
							onLinkClickHandleAnalytics={(analyticsEvent) => {
								fireUIAnalytics(analyticsEvent, SCREEN_SCHEME);
							}}
						/>
					),
				},
				{
					key: 'workflowTransitions',
					content: (
						<ListWorkflows
							workflowTransitions={[
								...new Map(
									workflowTransitions.values.map((workflowTransition) => [
										workflowTransition.workflowTransitionName + workflowTransition.workflowName,
										workflowTransition,
									]),
								).values(),
							]}
						/>
					),
				},
				{
					key: 'actions',
					content: (
						<ContextualAnalyticsData
							attributes={getAnalyticsAttributes(screen)}
							containers={{ screen: { id: `${id}` } }}
						>
							<ActionsCell
								id={id}
								actionLinks={buildActionLinks(screen, formatMessage)}
								onMenuClickHandleAnalytics={(analyticsEvent: UIAnalyticsEvent) =>
									fireUIAnalytics(analyticsEvent, SCREEN_ACTIONS)
								}
							/>
						</ContextualAnalyticsData>
					),
				},
			],
		};
	});

	// @ts-expect-error - TS7031 - Binding element 'sortOrder' implicitly has an 'any' type. | TS2304 - Cannot find name 'UIAnalyticsEventInterface'.
	const onSort = ({ sortOrder }, uiAnalytics: undefined | UIAnalyticsEvent) => {
		const orderBy = sortOrder === 'ASC' ? 'name' : '-name';
		if (uiAnalytics) {
			fireUIAnalytics(uiAnalytics, SCREENS_COLUMN, {
				columnName: 'name',
				sortOrder: sortOrder === 'ASC' ? ORDER.ASC : ORDER.DESC,
			});
		}
		getScreens({ ...filter, pageNumber: 1, orderBy });
	};
	const onSetPage = (pageNumber: number, uiAnalytics: undefined | UIAnalyticsEvent) => {
		if (uiAnalytics) {
			fireUIAnalytics(uiAnalytics, PAGE);
		}
		getScreens({ ...filter, pageNumber });
	};

	return (
		<TableWrapper>
			<DynamicTableStateless
				testId="admin-pages-screens-page.ui.screens-table.dynamic-table-stateless"
				head={header}
				rows={rows}
				page={filter.pageNumber}
				totalRows={total}
				rowsPerPage={filter.pageSize}
				loadingSpinnerSize="large"
				isLoading={isLoading}
				sortKey="name"
				sortOrder={filter.orderBy?.includes('-') ? 'DESC' : 'ASC'}
				onSort={onSort}
				onSetPage={onSetPage}
				emptyView={
					<ContextualAnalyticsData sourceType={SCREEN} sourceName="EmptyViewScreensTable">
						<EmptyState imageUrl={EmptyIcon} header={formatMessage(messages.empty)} size="narrow" />
						<FireScreenAnalytics />
					</ContextualAnalyticsData>
				}
			/>
		</TableWrapper>
	);
}
